import { Link } from 'react-router-dom';
import { ChevronRightIcon } from 'lucide-react';
import { useCatalog } from '../../../contexts/CatalogContext';
import { useSignedInAccount } from '../../../contexts/AccountContext';
import { useFeatures } from '../../../hooks/useFeatures';
import { useSkinPlan } from '../../../hooks/useSkinPlan';
import { plural } from '../../../core/format/text';
import { courseStatus, dayLong, dayShort, lastVisit, relative, upcomingAppointments } from '../../../utils/account';
import { ACCOUNT_SALON, ACCOUNT_THUIS, DELIVERY_HREF } from '../useAccountNav';
type Row = {
  to: string;
  label: string;
  detail: string;
};

/**
 * "Verder bij jou": per onderdeel één regel met de stand van zaken, en een tik naar de tab.
 * Geen kaarten: een lijst met dunne lijnen, zodat de volgende stap erboven blijft winnen.
 */
export function OverviewLinks() {
  const {
    account
  } = useSignedInAccount();
  const {
    treatmentBySlug
  } = useCatalog();
  const features = useFeatures();
  const {
    items,
    shape
  } = useSkinPlan();
  const next = upcomingAppointments(account.appointments)[0];
  const cs = courseStatus(account);
  const visit = lastVisit(account);
  const sub = features.subscriptions ? account.subscription : null;
  const lastDiary = [...account.diary].sort((a, b) => b.date.localeCompare(a.date))[0];
  const salonDetail = next ? `${dayLong(next.date)}, ${next.time} · ${treatmentBySlug(next.treatmentSlug)?.name ?? 'afspraak'}` : cs && !cs.finished ? `Kuur: ${cs.course.done} van ${cs.course.total} gehad` : visit ? `Geen afspraak gepland. Laatst: ${relative(visit.date)}` : 'Een behandeling of online huidcheck plannen';
  const homeDetail = sub?.status === 'actief' ? `Volgende levering ${dayShort(sub.nextDelivery)}` : sub?.status === 'gepauzeerd' ? `Levering gepauzeerd${sub.pausedUntil ? ` tot en met ${dayShort(sub.pausedUntil)}` : ''}` : items.length ? `${plural(items.length, 'product', 'producten')} in je routine` : account.orders.length ? `${plural(account.orders.length, 'bestelling', 'bestellingen')}` : 'Een routine voor thuis kiezen';
  const rows: Row[] = [{
    to: ACCOUNT_SALON,
    label: 'Salon',
    detail: salonDetail
  }, {
    to: sub && sub.status !== 'gestopt' ? DELIVERY_HREF : ACCOUNT_THUIS,
    label: 'Thuis',
    detail: homeDetail
  }, ...(features.diary && (items.length > 0 || shape.salon) ? [{
    to: '/account/dagboek',
    label: 'Fotodagboek',
    detail: lastDiary ? `Laatste foto ${relative(lastDiary.date)}` : 'Eén foto nu, over drie maanden zie je het verschil'
  }] : [])];
  return <section aria-labelledby="overview-more" data-id="element-6116" data-mp="2v708uukjegjl">
      <h2 id="overview-more" className="text-[15px] text-ink" data-id="element-6117" data-mp="2v3kqnivkgrdy">
        Verder bij jou
      </h2>
      <ul className="mt-3 divide-y divide-line border-y border-line" data-id="element-6118" data-mp="2q513xg95fbkj">
        {rows.map(r => <li key={r.label} data-id="element-6119" data-mp="2l9lud0i6c1tx">
            <Link to={r.to} className="group flex items-center gap-4 py-4 transition-colors duration-150 ease-soft hover:bg-sand/50 sm:px-2" data-id="element-6120" data-mp="2g868x7c33tb0">
              <span className="min-w-0 flex-1" data-id="element-6121" data-mp="2648107umqeuy">
                <span className="block text-[15px] text-ink" data-id="element-6122" data-mp="2b3kmop9rlfvy">{r.label}</span>
                <span className="mt-0.5 block text-[14px] text-muted first-letter:uppercase" data-id="element-6123" data-mp="2b5nkhvh5sdb8">{r.detail}</span>
              </span>
              <ChevronRightIcon className="h-4 w-4 shrink-0 text-muted transition-transform duration-150 ease-soft group-hover:translate-x-0.5" strokeWidth={1.5} aria-hidden="true" data-id="element-6124" data-mp="2l2jts3evt3gu" />
            </Link>
          </li>)}
      </ul>
    </section>;
}